import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useRegion } from "@/context/RegionContext";
import { useLanguage } from "@/context/LanguageContext";
import { ArticleCard } from "@/components/ArticleCard";
import { AnalystSpotlight } from "@/components/AnalystSpotlight";
import { CLBadge } from "@/components/Badges";
import { SignalBadge } from "@/components/SignalBadge";
import { IMAGES } from "@/lib/constants";
import { convictionBadgeClass } from "@/lib/conviction";
import { timeAgo } from "@/lib/format";
import { ArrowRight, Zap, TrendingUp, ArrowUpRight } from "lucide-react";

function SectionHead({ label, title, to }) {
  return (
    <div className="flex items-end justify-between mb-8">
      <div>
        <p className="font-mono-cl text-[11px] tracking-[0.28em] text-[#ccff00] uppercase mb-2">{label}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{title}</h2>
      </div>
      {to && (
        <Link to={to} className="hidden sm:flex items-center gap-1.5 text-sm text-zinc-400 hover:text-[#ccff00] transition-colors group">
          View all <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const { region } = useRegion();
  const { lang } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [matters, setMatters] = useState([]);
  const [research, setResearch] = useState([]);
  const [conviction, setConviction] = useState([]);
  const [analysts, setAnalysts] = useState([]);

  useEffect(() => {
    client.get(`/articles?region=${region}&lang=${lang}&limit=30`).then((r) => setArticles(r.data)).catch(() => {});
    client.get(`/articles/what-matters?lang=${lang}`).then((r) => setMatters(r.data)).catch(() => {});
    client.get(`/articles?type=research&lang=${lang}&limit=4`).then((r) => setResearch(r.data)).catch(() => {});
    client.get("/conviction").then((r) => setConviction(r.data)).catch(() => {});
    client.get("/analysts").then((r) => setAnalysts(r.data)).catch(() => {});
  }, [region, lang]);

  const hero = articles.find((a) => a.featured) || articles[0];
  const secondary = articles.filter((a) => a.slug !== hero?.slug).slice(0, 4);

  return (
    <div>
      {/* TOP PRIORITY: Analyst Partners (Skillex-style spotlight) */}
      <AnalystSpotlight analysts={analysts} />

      {/* OnchainLyfe Conviction */}
      <section className="border-b border-white/10 bg-[#070707]">
        <div className="max-w-[1400px] mx-auto px-6 py-10">
          <SectionHead label="Track Record · Live Performance" title="OnchainLyfe Conviction" to="/conviction" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {conviction.slice(0, 4).map((c) => {
              const neg = (c.performance_value ?? 0) < 0;
              return (
                <Link key={c.slug} to={`/conviction/${c.slug}`} data-testid={`conviction-top-${c.slug}`} className="cl-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-display text-xl font-bold">{c.asset}</span>
                    <span className={`font-mono-cl text-[10px] tracking-[0.12em] uppercase px-2 py-1 rounded-full border ${convictionBadgeClass(c.status)}`}>{c.status}</span>
                  </div>
                  <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{c.title}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-600 flex items-center gap-1.5">
                        {c.live && <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#ccff00]" />}
                        {c.live ? "Live · since research" : "Since research"}
                      </p>
                      <p className={`font-mono-cl text-2xl font-bold ${neg ? "text-red-400" : "text-[#ccff00]"}`}>{c.performance || "-"}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-zinc-600" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden grain border-b border-white/10">
        <div className="absolute inset-0">
          <img src={IMAGES.neon} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/85 to-[#050505]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 pt-20 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <span className="live-dot w-2 h-2 rounded-full bg-[#ccff00]" />
            <span className="font-mono-cl text-[11px] tracking-[0.24em] text-[#ccff00] uppercase">
              Breaking Intelligence · Live
            </span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">
            KNOW BEFORE THE <span className="text-[#ccff00] text-glow">MARKET MOVES.</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
            Crypto never sleeps. Unfortunately, neither do we. Research, on-chain moves & market stories worth knowing.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/research" data-testid="hero-explore" className="px-6 py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter] flex items-center gap-2">
              <Zap className="w-4 h-4" /> Explore Intelligence
            </Link>
            <Link to="/pricing" data-testid="hero-join" className="px-6 py-3 rounded-full border border-white/15 text-white font-semibold text-sm hover:border-[#ccff00]/50 transition-colors">
              Join Alpha
            </Link>
          </div>

          {/* hero story + what matters */}
          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 mt-14">
            {hero && (
              <Link to={`/article/${hero.slug}`} data-testid="hero-story" className="cl-card group overflow-hidden">
                <div className="relative h-72 sm:h-96 overflow-hidden">
                  <img src={hero.hero_image} alt={hero.title} className="w-full h-full object-cover opacity-85 group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    {(hero.badges || []).slice(0, 3).map((b) => <CLBadge key={b} label={b} />)}
                  </div>
                  {hero.signal_score != null && (
                    <div className="absolute top-4 right-4"><SignalBadge score={hero.signal_score} /></div>
                  )}
                  <div className="absolute bottom-0 p-6">
                    <p className="font-mono-cl text-[11px] tracking-[0.18em] uppercase text-[#ccff00] mb-2">{hero.category} · {timeAgo(hero.published_at)}</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight group-hover:text-[#ccff00] transition-colors">{hero.title}</h2>
                    <p className="text-zinc-300 mt-2 max-w-xl line-clamp-2">{hero.summary}</p>
                  </div>
                </div>
              </Link>
            )}

            <div className="cl-card p-6" data-testid="what-matters-now">
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp className="w-4 h-4 text-[#ccff00]" />
                <p className="font-mono-cl text-[11px] tracking-[0.24em] uppercase text-[#ccff00]">What Matters Now</p>
              </div>
              <div className="space-y-1">
                {matters.map((m, i) => (
                  <Link key={m.slug} to={`/article/${m.slug}`} className="group flex gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className="font-display text-2xl font-black text-zinc-700 group-hover:text-[#ccff00] transition-colors w-8">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-zinc-100 group-hover:text-[#ccff00] transition-colors leading-snug">{m.title}</p>
                      <p className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-500 mt-1">{m.category}</p>
                    </div>
                  </Link>
                ))}
                {matters.length === 0 && <p className="text-sm text-zinc-500">No stories yet.</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6">
        {/* FEATURED RESEARCH */}
        <section className="py-8">
          <SectionHead label="Institutional-Grade" title="Featured Research" to="/research" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {research.map((a) => <ArticleCard key={a.slug} a={a} />)}
          </div>
        </section>

        {/* SECONDARY NEWS */}
        <section className="py-8">
          <SectionHead label="The Feed" title="Latest Intelligence" to="/news" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {secondary.map((a) => <ArticleCard key={a.slug} a={a} />)}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="relative rounded-3xl border border-[#ccff00]/30 overflow-hidden grain glow-brand">
            <div className="absolute inset-0"><img src={IMAGES.neon2} alt="" className="w-full h-full object-cover opacity-20" /></div>
            <div className="relative p-10 sm:p-16 text-center">
              <p className="font-mono-cl text-[11px] tracking-[0.28em] text-[#ccff00] uppercase mb-3">The Network Behind The Research</p>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight max-w-3xl mx-auto leading-tight">
                Where crypto people share alpha.
              </h2>
              <p className="text-zinc-300 mt-4 max-w-xl mx-auto">
                Research, market insights, and on-chain updates from the OnchainLyfe network.
              </p>
              <Link to="/pricing" className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full bg-[#ccff00] text-black font-semibold hover:brightness-110 transition-[filter]">
                Request Access <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
